import "./Login.scss";

import { yupResolver } from "@hookform/resolvers/yup";
import axios from "axios";
import moment, { Moment } from "moment";
import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { BiCopyAlt, BiEnvelopeOpen } from "react-icons/bi";
import { useDispatch } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";

import { useAppSelector } from "../../app/hook";
import {
  usePassLessLoginFinalizeMutation,
  usePassLessStatusMutation
} from "../../app/services/auth-rtk.service";
import AuthLinkTo from "../../components/common/AuthLinkTo";
import LanguageChangeDropdownShort from "../../components/common/LanguageChangeDropdownShort";
import { MultiLanguageUrl } from "../../constants/multiLanguage.constant";
import { login, selectUserAuthStatus } from "../../features/auth/auth.slice";

type PassLessLoginType = {
  user_email: string;
  user_identification?: string;
  serverValidationToken: string;
  secretkey: string;
};

// Function to set session storage with an expiry time
function setSessionStorageWithExpiry(key: string, value: string, ttl: number) {
  const now = new Date();
  const item = {
    value: value,
    expiry: now.getTime() + ttl
  };
  sessionStorage.setItem(key, JSON.stringify(item));
}

// Function to get session storage and check if it's expired
function getSessionStorageWithExpiry(key: string) {
  const itemStr = sessionStorage.getItem(key);
  if (!itemStr) {
    return null;
  }
  const item = JSON.parse(itemStr);
  const now = new Date();
  if (now.getTime() > item.expiry) {
    sessionStorage.removeItem(key);
    return null; // Item has expired
  }
  return item.value; // Item is valid
}

const PasslessLogin = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location: any = useLocation();

  const isAuthentication = useAppSelector(selectUserAuthStatus);
  const [secretWord, setSecretWord] = useState<string | null>(null);

  const [passlessStatus] = usePassLessStatusMutation();
  const [passlessFinalize, passlessFinalizeResults] =
    usePassLessLoginFinalizeMutation();

  const MAX_ALLOWED_RETRIES = 3;

  const [error, setError] = useState(null);
  const [requestIsInProgress, setRequestIsInProgress] = useState(false);
  const [intervalID, setIntervalID] = useState(0);
  const [waitingForConfirm, setWaitingForConfirm] = useState(false);
  const [retries, setRetries] = useState(0);

  const from =
    location.state?.from?.pathname ||
    `/${MultiLanguageUrl[i18n.language] || "en-US"}/control-panel`;

  const schema = Yup.object().shape({
    user_email: Yup.string()
      .email("Invalid email")
      .required(t("PasslessLogin/key4"))
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues
  } = useForm<PassLessLoginType>({
    defaultValues: {},
    resolver: yupResolver(schema) as any
  });

  function generateRandomString() {
    const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let result = "";
    for (let i = 0; i < 4; i++) {
      const randomIndex = Math.floor(Math.random() * characters.length);
      result += characters[randomIndex];
    }
    return result;
  }

  const lastConfirmSecretWord = useCallback(
    async (values: PassLessLoginType) => {
      passlessFinalize({
        user_identification: values.user_identification as string,
        passless_token: values.serverValidationToken,
        secretkey: values.secretkey
      })
        .unwrap()
        .then((payload) => {
          localStorage.setItem(
            `${import.meta.env.VITE_STORAGE_PREFIX}_token`,
            payload.token
          );
          localStorage.setItem(
            `${import.meta.env.VITE_STORAGE_PREFIX}_refresh_token`,
            payload.refresh_token
          );

          dispatch(
            login({
              is_remember_me: false
            })
          );

          setRequestIsInProgress(false);

          return navigate(
            `/${MultiLanguageUrl[i18n.language] || "en-US"}/control-panel`,
            {
              replace: true
            }
          );
        })
        .catch((err: any) => {
          switch (err.data?.code) {
            case "RSUSKO014":
            case "RSUSKO019":
            case "RSUSKO020":
            case "RSUSKO021":
            case "RSUSKO023":
            case "RSUSKO015":
              setWaitingForConfirm(false);
              break;
            default:
              break;
          }

          setRequestIsInProgress(false);

          setError(err.data.message);

          window.scrollTo(0, 0);
        });
    },
    [navigate, passlessFinalize]
  );

  const waitForConfirmation = useCallback(
    async (values: PassLessLoginType, endTime: Moment, secretkey: string) => {
      if (values.user_identification) {
        const interval = setInterval(() => {
          setIntervalID(+interval);

          if (secretkey) {
            passlessStatus({
              user_identification: values.user_identification as string,
              secret_word: secretkey as string
            })
              .unwrap()
              .then(async (payload) => {
                try {
                  if (payload.code === "RSUSOK002" && payload.element) {
                    setWaitingForConfirm(false);
                    lastConfirmSecretWord({
                      ...values,
                      serverValidationToken: payload.element
                        .serverValidationToken as string
                    });

                    clearInterval(interval);
                  }
                } catch (err: any) {
                  if (err.data?.code !== "RSUSKO022")
                    toast.error(err.data?.message || err.message);
                }
              })
              .catch((err: any) => {
                if (
                  err.status !== 401 ||
                  err.data?.code !== "RSUSKO022" ||
                  endTime.isBefore(moment())
                ) {
                  setWaitingForConfirm(false);
                  clearInterval(interval);

                  setRequestIsInProgress(false);

                  setError(err.data.message);

                  window.scrollTo(0, 0);

                  return;
                }

                switch (err.data?.code) {
                  case "RSUSKO014":
                  case "RSUSKO019":
                  case "RSUSKO020":
                  case "RSUSKO021":
                  case "RSUSKO023":
                  case "RSUSKO015":
                    setWaitingForConfirm(false);
                    clearInterval(interval);
                    setRequestIsInProgress(false);
                    break;
                  default:
                    break;
                }
              });
          }
        }, 5000);
      }
    },
    [secretWord]
  );

  const onSubmit = (data: PassLessLoginType) => {
    const maxPasslessLoginReached = getSessionStorageWithExpiry(
      "maxPasslessLoginReached"
    );

    if (retries >= MAX_ALLOWED_RETRIES || maxPasslessLoginReached) {
      if (!maxPasslessLoginReached)
        setSessionStorageWithExpiry(
          "maxPasslessLoginReached",
          "true",
          2 * 60000 // two minutes simulator fot rate limiter
        );

      toast.error(t("PasslessLogin/key6"));

      return navigate(`/${MultiLanguageUrl[i18n.language] || "en-US"}/login`, {
        replace: true
      });
    }

    setRetries(retries + 1);

    setRequestIsInProgress(true);

    const secretKey = generateRandomString();

    setSecretWord(secretKey);

    axios
      .post(
        `${import.meta.env.VITE_SUM_C_DOMAIN}/auth/${
          import.meta.env.VITE_LOUISE_ENTITY_ID
        }/login/passless`,
        { secret_word: secretKey, user_email: data.user_email },
        {
          headers: {
            "Content-Type": "application/json"
          }
        }
      )
      .then(async (payload: any) => {
        try {
          if (payload.data.code === "RSUSOK001") {
            data.user_identification = payload.data.element.userIdentification;

            await waitForConfirmation(
              data,
              moment().add(import.meta.env.VITE_PASSLESS_TIMEOUT, "minutes"),
              secretKey
            );

            setWaitingForConfirm(true);
          }
        } catch (err: any) {
          toast.error(err.data?.message || err.message);
        }
      })
      .catch((err) => {
        setError(err.data?.message || err.message);
      });
  };

  const handleCancel = () => {
    setWaitingForConfirm(false);
    setError(null);
    clearInterval(intervalID);
    setIntervalID(0);
    setRequestIsInProgress(false);
  };

  const copySecretWord = () => {
    navigator.clipboard.writeText(secretWord as string);
    toast.success(t("PasslessLogin/key14"));
  };

  // Effect to clear interval when the component unmounts
  useEffect(() => {
    return () => {
      if (intervalID) {
        clearInterval(intervalID); // Clear interval on component unmount
      }
    };
  }, [intervalID]);

  if (isAuthentication) {
    return navigate(from);
  }

  return (
    <React.Fragment>
      <div className="tw-bg-gray-100  tw-min-h-screen tw-h-full tw-flex tw-flex-col tw-justify-start tw-items-center md:tw-p-4  ">
        <div className="tw-text-center md:tw-h-16 tw-h-full tw-bg-gray-100 tw-my-2">
          <Link to="https://louisebot.com">
            <div className=" tw-justify-start tw-items-center tw-gap-1 tw-inline-flex">
              <img
                alt="LouiseBot"
                src="https://cdn.1cdn.app/application/LOUISEBOT/23091707385795_Logo_colorful.svg"
                className="tw-w-10 tw-h-10 tw-relative"
              />
              <div className="tw-text-blue-700 tw-text-lg tw-font-extrabold tw-font-['Lexend'] tw-leading-7">
                LouiseBot
              </div>
            </div>
          </Link>
        </div>
        <div className="md:tw-px-24 md:tw-w-3/4 tw-w-full md:tw-pt-[126px] tw-p-7 tw-bg-white tw-rounded-2xl tw-grid tw-grid-cols-2 tw-gap-6">
          <div className="md:tw-col-span-1 tw-col-span-2 md:tw-block tw-hidden">
            <div className="tw-self-stretch tw-text-gray-800 tw-text-4xl tw-font-bold tw-font-['Lexend'] tw-leading-10">
              {t("PasslessLogin/key1")}
            </div>
            <div className="tw-self-stretch tw-text-gray-800 tw-text-lg tw-font-normal tw-font-['Lexend'] tw-leading-9 tw-mt-5">
              {t("PasslessLogin/key2")}
            </div>
            <div className="tw-self-stretch tw-text-gray-800 tw-text-lg tw-font-normal tw-font-['Lexend'] tw-leading-9">
              {t("PasslessLogin/key2-1")}
            </div>
          </div>
          <div className="md:tw-col-span-1 tw-col-span-2">
            <div className="tw-w-full">
              <div className="">
                <div className="md:tw-hidden tw-flex mb-4  justify-content-center align-items-center text-center tw-flex-col">
                  <h1 className=" register-title text-center font-sofiaSans w-100 tw-text-[30px]">
                    {t("PasslessLogin/key1")}
                  </h1>
                </div>

                {error && (
                  <Alert variant="danger" className="tw-mb-3">
                    {error}
                  </Alert>
                )}
                <Form onSubmit={handleSubmit(onSubmit)} className="login-form">
                  <Form.Group className="mb-3">
                    <Form.Label htmlFor="user_email" className="login-label">
                      {t("authPages/login/l-01")}
                    </Form.Label>
                    <Form.Control
                      className="login-input"
                      id="user_email"
                      type="email"
                      {...register("user_email")}
                    />
                    <Form.Control.Feedback
                      type="invalid"
                      className="d-block font-poppins"
                    >
                      {errors.user_email?.message as string}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <div className="tw-flex tw-flex-col tw-gap-3 tw-w-full">
                    <Button
                      variant="primary"
                      type="submit"
                      className="w-100 d-flex justify-content-center"
                      size="lg"
                      disabled={requestIsInProgress}
                    >
                      {requestIsInProgress ? (
                        <Spinner size="sm" />
                      ) : (
                        t("authPages/login/btn")
                      )}
                    </Button>

                    <AuthLinkTo
                      to="login"
                      className="tw-p-3 tw-bg-blue-100 tw-rounded-xl tw-shadow-sm tw-justify-center tw-items-center tw-inline-flex tw-overflow-hidden"
                    >
                      <span className="tw-text-center tw-text-blue-800 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                        {t("PasslessLogin/key7")}
                      </span>
                    </AuthLinkTo>
                  </div>
                </Form>
              </div>
            </div>
          </div>
          <div className="tw-col-span-2 md:tw-mt-[88px] tw-mt-[20px] tw-text-zinc-500 tw-text-[8px] tw-font-thin tw-font-['Roboto'] tw-leading-tight tw-text-center">
            © LouiseBot {new Date().getFullYear()} -{" "}
            {import.meta.env.VITE_IMAGE_VERSION}
          </div>
        </div>

        <div className="tw-bg-gray-100 tw-w-full tw-flex tw-justify-center tw-items-center tw-p-2">
          <LanguageChangeDropdownShort theme={"bg-eightColor"} />
        </div>
      </div>

      {/* Waiting for passless resutl */}
      <Modal show={waitingForConfirm} centered backdrop="static">
        <Modal.Body className="tw-p-0">
          <div className="tw-w-full tw-bg-white tw-rounded-2xl tw-shadow-xl tw-flex-col tw-justify-start tw-items-center tw-inline-flex tw-overflow-hidden">
            <div className="tw-self-stretch tw-px-4 tw-pt-6 tw-pb-10 tw-flex-col tw-justify-start tw-items-center tw-gap-3 tw-flex">
              <div className="tw-w-10 tw-h-10 tw-p-2.5 tw-bg-amber-100 tw-rounded-3xl tw-justify-center tw-items-center tw-inline-flex">
                <BiEnvelopeOpen size={20} className="tw-text-amber-600" />
              </div>
              <div className="tw-self-stretch tw-h-[104px] tw-flex-col tw-justify-start tw-items-center tw-gap-2 tw-flex">
                <div className="tw-self-stretch tw-text-center tw-text-gray-700 tw-text-lg tw-font-semibold tw-leading-7">
                  {t("PasslessLogin/key8")}
                </div>
                <div className="tw-self-stretch tw-text-center tw-text-gray-500 tw-text-sm tw-font-normal tw-leading-tight">
                  {t("PasslessLogin/key9")} {getValues("user_email")}
                  <br />
                </div>
                <div className="tw-self-stretch tw-text-center tw-text-gray-500 tw-text-sm tw-font-normal tw-leading-tight">
                  {t("PasslessLogin/key10")}
                </div>
              </div>
              <div className="tw-h-24 tw-w-full tw-px-8 tw-py-6 tw-bg-gray-50 tw-rounded-2xl tw-border tw-border-gray-300 tw-flex-col tw-justify-start tw-items-start tw-gap-4 tw-inline-flex">
                <div className="tw-self-stretch tw-justify-between tw-items-center tw-inline-flex">
                  <div className="tw-flex-col tw-justify-center tw-items-start tw-inline-flex">
                    <div className="tw-text-gray-600 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                      {t("PasslessLogin/key12")}
                    </div>
                    <div className="tw-text-gray-800 tw-text-2xl tw-font-normal tw-font-['Lexend'] tw-leading-loose">
                      {secretWord}
                    </div>
                  </div>
                  <div
                    onClick={copySecretWord}
                    className="tw-cursor-pointer tw-px-5 tw-py-2 tw-bg-primary tw-rounded-full tw-shadow-sm tw-justify-center tw-items-center tw-gap-2 tw-flex tw-overflow-hidden"
                  >
                    <BiCopyAlt className="tw-text-white tw-w-4 tw-h-4 tw-relative tw- tw-overflow-hidden" />
                    <div className="tw-text-white tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                      {t("PasslessLogin/key13")}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="tw-self-stretch tw-h-[66px] tw-px-4 tw-py-3 tw-bg-gray-50 tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-flex">
              <button
                onClick={handleCancel}
                className="tw-self-stretch tw-px-[17px] tw-py-[9px] tw-border-none tw-bg-white tw-rounded-[99px] tw-shadow-sm tw-border tw-border-gray-300 tw-justify-center tw-items-center tw-inline-flex tw-overflow-hidden"
                disabled={passlessFinalizeResults.isLoading}
              >
                <span className="tw-text-gray-700 tw-text-base tw-font-normal tw-leading-normal">
                  {t("PasslessLogin/key11")}
                </span>
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </React.Fragment>
  );
};

export default PasslessLogin;
